import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import _ from 'underscore';
import { Stack } from '@fluentui/react';
import { Alert } from '@bingads-webui-react/alert';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { withScope } from '@bingads-webui-react/scope-service';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SEARCH_TYPE } from '@bingads-webui-react/bing-maps';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { useFormService } from '@bingads-webui-react/form';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors, andComp, anyComp, entityIs, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { Collapse, TextTitle, ValidatedBootstrapPanel, ValidatedFixedPanel, ValidatedPanel, collapseExpander, useCollapseState, getPanelErrorProps } from '@bingads-webui-campaign/wizard-jumbo-card';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ValidatedLocationTargetSetting } from '@bingads-webui-campaign-react/step-wizard-controls';
import { getAdScheduleDetails, getLocationTargetTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { ValidatedLegacyDateSetting } from '@bingads-webui-campaign/date-setting';
import { ValidatedLegacyAdSchedule } from '@bingads-webui-campaign/ad-schedule';
import { AgeRange, Gender, formatAgeRanges, formatGenders } from '@bingads-webui-campaign-react/xandr-campaign';
import { StepHeader, ERROR_ENTITY, adScheduleIcon, ageTargetIcon, genderTargetIcon, locationTargetIcon, startDateEndDateIcon, SectionShimmer, getTargetSettingsFromCriterions, PredictiveTargetingHeader } from '@bingads-webui-campaign-react/display-plus-campaigns';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';

import {
  TargetingPreferences,
} from './components';
import { getTargetingPanels, getAdGroupStyles } from './utils';
import { isSearchShoppingCampaign } from '../step-utils';
import { ValidatedLegacyAdDistribution } from './components/ad-distribution';

const campaignLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.CampaignLocation),
  entityIs(ERROR_ENTITY.CampaignRadius),
  entityIs(ERROR_ENTITY.CampaignNegativeLocation),
  entityIs(ERROR_ENTITY.CampaignLocationIntent)
);

const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(
  ValidatedLocationTargetSetting,
  { filter: campaignLocationCriterions }
));

const ServerValidatedLocationTargetSettingForAdGroup = withControlErrorHandling(withUnvalidatedControlErrors(
  ValidatedLocationTargetSetting,
  {
    filter: anyComp(
      entityIs(ERROR_ENTITY.AdGroupLocation),
      entityIs(ERROR_ENTITY.AdGroupRadius),
      entityIs(ERROR_ENTITY.AdGroupNegativeLocation),
      entityIs(ERROR_ENTITY.AdGroupLocationIntent)
    ),
  }
));

const ServerValidatedAdDistributionSetting = withControlErrorHandling(withUnvalidatedControlErrors(
  ValidatedLegacyAdDistribution,
  {
    filter: andComp(entityIs(ERROR_ENTITY.AdGroup), propertyIs('adDistribution')),
  }
));

const adGroupLevelTargetListForAudienceFlow = [
  'locationTargets', 'genderTypes', 'ageRanges', 'companyNameTargets', 'negativeCompanyNameTargets',
  'industry', 'negativeIndustry', 'jobTargets', 'negativeJobTargets', 'audiences',
];
const adGroupPropertyList = [
  'UseOptimizedTargeting', 'StartDate', 'EndDate', 'AdDistribution',
];

const TargetStepInternal = ({
  data,
  onDataChange,
  scope,
  error,
  flowType,
  permissions,
}) => {
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const classes = useDefaultStyles(getAdGroupStyles);
  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();
  const isAudienceCampaign = !isSearchShoppingCampaign(flowType);
  const { AgeGenderDisclaimer: isAgeGenderDisclaimerEnabled } = _.result(permissions, 'dynamic', {});

  const {
    campaign,
    adGroup,
    bundleCountries,
    bundleLocationCriterions,
  } = data || {};
  const {
    locationTargets: campaignLocationTargets,
    genderTypes: campaignGenderTypes,
    ageRanges: campaignAgeRanges,
  } = campaign || {};
  const {
    locationTargets: adGroupLocationTargets,
    genderTypes: adGroupGenderTypes,
    ageRanges: adGroupAgeRanges,
  } = adGroup || {};
  const genderTypes = isAudienceCampaign ? adGroupGenderTypes : campaignGenderTypes;
  const ageRanges = isAudienceCampaign ? adGroupAgeRanges : campaignAgeRanges;
  const locationTargets = isAudienceCampaign ? adGroupLocationTargets : campaignLocationTargets;

  const serverContext = useContext(ServerContext);
  const { basicOptions, moreOptions } = getTargetingPanels(flowType, serverContext);
  const [
    allTargetSettingsSections,
    targetSettingsExpandedSections,
    setTargetSettingsExpandedSections,
  ] = useCollapseState(
    basicOptions /* all sections */,
    ['ad-group-name', 'location-target', 'predictive-targeting'] /* initially expanded sections */
  );
  const [
    allMoreSettingsSections,
    moreSettingsExpandedSections,
    setMoreSettingsExpandedSections,
  ] = useCollapseState(
    moreOptions /* all sections */,
    ['start-end-dates', 'ad-schedule', 'ad-distribution'] /* initially expanded sections */
  );
  const allBasicExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setTargetSettingsExpandedSections }),
    [setTargetSettingsExpandedSections]
  );
  const [adScheduleView, setAdScheduleView] = React.useState(null);
  const [adDistributionView, setAdDistributionView] = React.useState(null);

  const onValidate = (result) => {
    if (data && data.campaign) {
      // Certain targets have to be created at ad group level for AWF
      const targets = _.pick(result.data, adGroupLevelTargetListForAudienceFlow);
      const adGroupProperties = _.pick(result.data, adGroupPropertyList);
      const campaignData = _.omit(result.data, adGroupLevelTargetListForAudienceFlow, adGroupPropertyList);

      const mergedData = {
        ...data,
        campaign: {
          ...data.campaign,
          ...campaignData,
          ...isAudienceCampaign ? {} : targets,
        },
        adGroup: {
          ...data.adGroup,
          ...isAudienceCampaign ? targets : {},
          ...adGroupProperties,
        },
      };

      if (isAudienceCampaign) {
        mergedData.adGroup.TargetSetting = getTargetSettingsFromCriterions(mergedData.adGroup); // For AWF, the default is target and bid
      }

      onDataChange({
        data: mergedData,
        isValid: result.isValid,
      });
    }
  };

  const locationTargetProps = {
    validationProps: {
      data: locationTargets,
    },
    allowNegativeLocationTargeting: true,
    isRadiusExcludeEnabled: false,
    bundleCountries,
    bundleLocationCriterions,
    hideIntent: isAudienceCampaign,
    defaultSelectedSearchType: SEARCH_TYPE.Boundary,
    bidStrategyType: 'placeholder',
    scope,
    strings: {
      description: i18n.getString(_TL_('For example, a country/region, state/province, or ZIP/postal code.')),
    },
  };

  return (
    <div className={classNames(classes.adgroupShell, 'target-shell', 'target-step')}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: error || [], showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                schema: AdGroup,
                data: (data && data.adGroup) || {},
                i18n: legacyI18n,
                onValidate,
              }}
            >
              <div>
                <Stack token={{ childrenGap: 14 }} >
                  <StepHeader
                    title={_TL_('Who do you want to see your ads?')}
                  />
                  <div className="server-errors">
                    <UnmappedFormErrors />
                  </div>
                </Stack>
              </div>
              <Stack className="base-options-stack" horizontal tokens={{ childrenGap: 14 }}>
                <Collapse allPanelItems={allTargetSettingsSections} activeKeys={targetSettingsExpandedSections} setActiveKeys={setTargetSettingsExpandedSections} checkItems skipItemsTypeCheck>
                  <ValidatedPanel
                    key="location-target"
                    data={data}
                    onDataChange={onDataChange}
                    actuationProps={{
                      expand: allBasicExpand,
                      expandKey: 'location-target',
                    }}
                    title={
                      <TextTitle
                        titleKey={_TL_('Location')}
                        key="location-target"
                        topicKey="pop_BA_CampaignCreate_Settings_LocationTargeting"
                        panelKey="location-target"
                        details={getLocationTargetTitleDetails(i18n, locationTargets, false, '', true)}
                        icon={locationTargetIcon}
                      />
                    }
                  >
                    {
                      isAudienceCampaign ? (
                        <ServerValidatedLocationTargetSettingForAdGroup
                          {...locationTargetProps}
                        />
                      ) : (
                        <ServerValidatedLocationTargetSetting
                          {...locationTargetProps}
                        />
                      )
                    }
                  </ValidatedPanel>
                  <ValidatedPanel
                    key="gender-target"
                    title={<TextTitle
                      titleKey={_TL_('Gender')}
                      key="gender-target"
                      panelKey="gender-target"
                      details={formatGenders(genderTypes, i18n)}
                      topicKey="pop_BA_CampaignCreate_AudTargets_Gender"
                      icon={genderTargetIcon}
                    />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    actuationProps={{
                      expand: allBasicExpand,
                      expandKey: 'gender-target',
                    }}
                  >
                    {
                      genderTypes ? (<Gender
                        i18n={i18n}
                        genders={genderTypes}
                        hideUnknown={!isAudienceCampaign}
                      />) : (<SectionShimmer />)
                    }
                  </ValidatedPanel>
                  <ValidatedPanel
                    key="age-target"
                    title={<TextTitle
                      titleKey={_TL_('Age')}
                      key="age-target"
                      topicKey="pop_BA_CampaignCreate_AudTargets_Age"
                      panelKey="age-target"
                      details={formatAgeRanges(ageRanges, i18n)}
                      icon={ageTargetIcon}
                    />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    actuationProps={{
                      expand: allBasicExpand,
                      expandKey: 'age-target',
                    }}
                  >
                    {
                      ageRanges ? (<AgeRange
                        i18n={i18n}
                        ageRanges={ageRanges}
                      />) : (<SectionShimmer />)
                    }
                  </ValidatedPanel>
                  {
                    isAudienceCampaign && (
                      <ValidatedFixedPanel
                        key="predictive-targeting"
                        title={data ? <PredictiveTargetingHeader data={adGroup} /> : <SectionShimmer count={1} />}
                      >
                        <div />
                      </ValidatedFixedPanel>
                    )
                  }
                </Collapse>
              </Stack>
              <Stack horizontal token={{ childrenGap: 14 }}>
                <TargetingPreferences data={data} flow={flowType} />
              </Stack>
              <Stack horizontal token={{ childrenGap: 14 }}>
                <ValidatedBootstrapPanel
                  defaultExpanded={false}
                  className={classNames(classes.collapsiblePanel, 'more-options-panel')}
                  render={() => (
                    <>
                      <BootstrapPanel.Heading>
                        <BootstrapPanel.Title toggle>
                          {i18n.getString(_TL_('More options'))}
                        </BootstrapPanel.Title>
                      </BootstrapPanel.Heading>
                      <BootstrapPanel.Collapse>
                        <BootstrapPanel.Body>
                          <div className={classNames('more-options-section')}>
                            <Collapse allPanelItems={allMoreSettingsSections} checkItems skipItemsTypeCheck activeKeys={moreSettingsExpandedSections} setActiveKeys={setMoreSettingsExpandedSections}>
                              <ValidatedPanel
                                key="start-end-dates"
                                title={<TextTitle
                                  titleKey={_TL_('Start and end dates')}
                                  icon={startDateEndDateIcon}
                                />}
                                attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                              >
                                {
                                  data && data.adGroup ? (
                                    <ValidatedLegacyDateSetting
                                      i18n={i18n}
                                      validationProps={{
                                        data: {
                                          StartDate: data.adGroup.StartDate,
                                          EndDate: data.adGroup.EndDate,
                                          TimeZone: data.campaign.Timezone,
                                          isCreate: !data.adGroup.Id,
                                        },
                                      }}
                                    />
                                  ) : (<SectionShimmer count={3} />)
                                }
                              </ValidatedPanel>
                              <ValidatedPanel
                                key="ad-schedule"
                                title={<TextTitle
                                  titleKey={_TL_('Ad schedule')}
                                  key="ad-schedule"
                                  panelKey="ad-schedule"
                                  icon={adScheduleIcon}
                                  details={`${getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current, true)}`}
                                />}
                              >
                                {
                                  campaign ? (
                                    <ValidatedLegacyAdSchedule
                                      isCreation
                                      hideMinuteOptions
                                      level="adgroup"
                                      entity={{ data: campaign }}
                                      validationProps={{
                                        data: campaign.adSchedules,
                                      }}
                                      hideUseCampaignSetting
                                      setAdScheduleView={setAdScheduleView}
                                    />
                                  ) : (<SectionShimmer />)
                                }
                              </ValidatedPanel>
                              <ValidatedPanel
                                key="ad-distribution"
                                title={<TextTitle
                                  titleKey={_TL_('Ad distribution')}
                                  details={adDistributionView ? adDistributionView.getTranslatedOption() : ''}
                                />}
                              >
                                <ServerValidatedAdDistributionSetting
                                  setAdDistributionView={setAdDistributionView}
                                />
                              </ValidatedPanel>
                            </Collapse>
                          </div>
                        </BootstrapPanel.Body>
                      </BootstrapPanel.Collapse>
                    </>
                  )}
                />
              </Stack>
              {
                isAgeGenderDisclaimerEnabled && (
                  <Alert
                    message={i18n.getString(_TL_('If you’re advertising in connection with any financial, insurance, education, career and employment, and/or housing services, you should <b>not</b> use individuals’ demographics—such as age, gender, location, etc.—for the purpose of personalizing advertising, segmenting, or profiling customers.'))}
                    level="Info"
                    icon={(<div className="alert-image" />)}
                    dismissible={false}
                  />
                )
              }
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

export const TargetStep = withPermissions(withScope(TargetStepInternal));
